import {
  LANGFUSE_METADATA_HEADER,
  buildLangfuseMetadata,
  createLangfuseRunId,
  encodeLangfuseMetadataHeader,
  type LangfusePostedObservation,
  type LangfuseRunSkill,
  postLangfuseObservation,
  postLangfuseOutcome,
  startLangfuseRun,
  toLangfuseRunSkills,
  withLangfuseMetadataHeader,
} from "../shared/services/langfuseTrace";
import type { PresentationContent } from "./types";

export { LANGFUSE_METADATA_HEADER, encodeLangfuseMetadataHeader, toLangfuseRunSkills };
export const PPTX_LANGFUSE_TRACE_NAME = "FactSetAddIn.PowerPoint.ai.run";

// Run phases for the PowerPoint AI pipeline. Entry phases (`planner`, `chat`,
// `deck_build`) surface their request as the trace input server-side; follow-up
// work such as executor hops and post-build polish must not clobber that input.
export type PptxLangfusePhase =
  | "planner"
  | "deck_build"
  | "chat"
  | "qa"
  | "polish"
  | "multi_hop_executor"
  | "outcome";

export type PptxLangfuseSurface = "task_pane" | "dialog";

export interface PptxLangfuseRunContext {
  runId: string;
  requestId: string;
  correlationId: string;
  sessionId?: string;
  /** Optional parent planner run id for deck-build correlation. */
  plannerRunId?: string;
  host: "powerpoint";
  surface: PptxLangfuseSurface;
  /** "chat" | "create" | "review" (chat edit / deck creation / deck QA). */
  mode: string;
  hasContext: boolean;
  deck?: {
    slideCount: number;
    windowedSlideCount?: number;
    selectedSlideIndex?: number;
  };
  traceId?: string;
  rootSpanId?: string;
  traceInput?: unknown;
  traceStartedAt?: string;
  /** The run's resolved Skills, stamped on the trace as `skill.*` by the server. */
  skills?: readonly LangfuseRunSkill[];
}

export interface PptxLangfuseOutcome {
  status: "completed" | "failed" | "cancelled";
  durationMs: number;
  errorType?: string;
  needsClarification?: boolean;
  deckEditAttempted?: boolean;
  deckEditSuccess?: boolean;
  slidesRequested?: number;
  slidesCreated?: number;
  slidesReviewed?: number;
  issuesFound?: number;
}

export interface PptxLangfuseObservation extends LangfusePostedObservation {}

export function createPptxLangfuseRunId(): string {
  return createLangfuseRunId("pptx");
}

export function summarizePresentationForLangfuse(
  presentationContent: PresentationContent | null | undefined,
): PptxLangfuseRunContext["deck"] | undefined {
  if (!presentationContent) return undefined;
  const windowedSlideCount = presentationContent.slides?.length ?? 0;
  const slideCount = presentationContent.totalDeckSlides ?? windowedSlideCount;
  return {
    slideCount,
    windowedSlideCount,
    ...(typeof presentationContent.selectedSlideIndex === "number"
      ? { selectedSlideIndex: presentationContent.selectedSlideIndex }
      : {}),
  };
}

export function createPptxLangfuseRunContext(params: {
  mode: string;
  hasContext: boolean;
  sessionId?: string;
  surface?: PptxLangfuseSurface;
  presentationContent?: PresentationContent | null;
  /** Optional explicit run id override for advanced callers/tests. */
  runId?: string;
  /** Optional planner run id to correlate deck-build traces. */
  plannerRunId?: string;
}): PptxLangfuseRunContext {
  const runId = params.runId || createPptxLangfuseRunId();
  return {
    runId,
    requestId: runId,
    correlationId: runId,
    // Never substitute runId: Langfuse sessionId is the persisted chat-tab ID.
    sessionId: params.sessionId,
    ...(params.plannerRunId ? { plannerRunId: params.plannerRunId } : {}),
    host: "powerpoint",
    surface: params.surface ?? "task_pane",
    mode: params.mode,
    hasContext: params.hasContext,
    deck: summarizePresentationForLangfuse(params.presentationContent),
  };
}

export async function startPptxLangfuseRun(
  context: PptxLangfuseRunContext,
  input: unknown,
): Promise<void> {
  await startLangfuseRun({
    endpoint: "/api/ai/langfuse/pptx-run-start",
    context,
    metadata: buildPptxLangfuseMetadata(context, { phase: "root" }),
    input,
    hostLabel: "PowerPoint",
  });
}

export function buildPptxLangfuseMetadata(
  context: PptxLangfuseRunContext | undefined,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> {
  return buildLangfuseMetadata(
    context || { host: "powerpoint", surface: "task_pane" },
    PPTX_LANGFUSE_TRACE_NAME,
    overrides,
  );
}

export function withPptxLangfuseMetadata(
  init: RequestInit,
  context: PptxLangfuseRunContext | undefined,
  overrides: Record<string, unknown> = {},
): RequestInit {
  return withLangfuseMetadataHeader(init, buildPptxLangfuseMetadata(context, overrides));
}

/**
 * Attaches the Langfuse metadata header only when a run context is present, so
 * out-of-scope PowerPoint provider calls (no instrumented run) stay untraced
 * server-side instead of creating ungrouped traces.
 */
export function withOptionalPptxLangfuseMetadata(
  init: RequestInit,
  context: PptxLangfuseRunContext | undefined,
  overrides: Record<string, unknown> = {},
): RequestInit {
  if (!context) return init;
  return withPptxLangfuseMetadata(init, context, overrides);
}

export function postPptxLangfuseOutcome(
  context: PptxLangfuseRunContext | undefined,
  outcome: PptxLangfuseOutcome,
): void {
  if (!context) return;

  postLangfuseOutcome({
    endpoint: "/api/ai/langfuse/pptx-run-outcome",
    metadata: buildPptxLangfuseMetadata(context, { phase: "outcome" }),
    outcome,
    input: context.traceInput,
    startTime: context.traceStartedAt,
    hostLabel: "PowerPoint",
  });
}

export function postPptxLangfuseObservation(
  context: PptxLangfuseRunContext | undefined,
  observation: PptxLangfuseObservation,
  options?: { phase?: PptxLangfusePhase }
): void {
  if (!context || !observation?.name?.trim()) return;

  postLangfuseObservation({
    endpoint: "/api/ai/langfuse/pptx-observation",
    metadata: buildPptxLangfuseMetadata(context, { phase: options?.phase ?? "multi_hop_executor" }),
    observation,
    hostLabel: "PowerPoint",
  });
}
